/**
 * training-wizard.js
 */

import { matchPath } from "react-router-dom";
import { parseISO, isAfter } from 'date-fns';
import { Types } from 'utils/constants';
import { paths, makeLocation } from 'utils/routing';
import { shouldRenderMobileLayout } from 'utils/app-context';
import TrainingWizardMenu from './training-wizard-menu';
import EventView from '../event/view';
import EventEdit from '../event/edit';
import EventContainer from '../event/container';
import ExerciseEdit from '../exercise/edit';
import ExerciseContainer from '../exercise/container';
import AnswerEdit from '../answer/edit';
import ExerciseView from '../exercise/view';


const findIncompleteAnswer = async (answers, afterId, recordStore) => {
  let i = afterId ? answers.indexOf(afterId) + 1 : 0;
  for (i; i < answers.length; i++) {
    const answerId = answers[i];
    const answer = await recordStore.getRecord(Types.Record.ANSWER, answerId);
    if (answer && answer.dog.appliesToExercise && !answer.dateCompleted) {
      return answer;
    }
  }

  return false;
}


const redirectToComplete = (basePath, history, eventId, exerciseId) => {
  const pathName = shouldRenderMobileLayout() 
    ? 'wizardCompleteMenu' 
    : exerciseId 
      ? 'wizardCompleteExercise'
      : 'wizardCompleteEvent';
  history.push(makeLocation(
    basePath,
    'training',
    pathName,
    {eventId, exerciseId},
    history.location
  ));
}


export default {
  getMenuRoutes: (basePath) => {
    return [{
      component: TrainingWizardMenu,
      path: [
        basePath + paths.training.base + paths.training.wizardPattern
      ]
    }]
  },

  getMobileRoutes: (basePath) => {
    const drawerBasePath = basePath + paths.training.base;
    return [{
      component: EventContainer,
      path: [
        drawerBasePath + paths.training.wizardStart.path,
        drawerBasePath + paths.training.wizardCreateEvent.path,
        drawerBasePath + paths.training.wizardSelectEvent.path
      ]
    }, {
      component: ExerciseContainer,
      path: [
        drawerBasePath + paths.training.wizardAddAnswer.path,
        drawerBasePath + paths.training.wizardAddExercise.path,
        drawerBasePath + paths.training.wizardEditExercise.path,
      ]
    }, {
      component: TrainingWizardMenu,
      path: [
        drawerBasePath + paths.training.wizardPattern + '/menu'
      ]
    }];
  },

  getDesktopRoutes: (basePath, user) => {
    const drawerBasePath = basePath + paths.training.base;
    // HANDLER WIZARD ROUTES
    if (user.role.isHandler) {
      return [{
        component: EventView,
        path: [
          drawerBasePath + paths.training.wizardCompleteEvent.path
        ]
      }, {
        component: EventEdit,
        path: [
          drawerBasePath + paths.training.wizardEditEvent.path
        ]
      }, {
        component: EventContainer,
        path: [
          drawerBasePath + paths.training.wizardStart.path,
          drawerBasePath + paths.training.wizardCreateEvent.path,
          drawerBasePath + paths.training.wizardSelectEvent.path
        ]
      }, {
        component: ExerciseContainer,
        path: [
          drawerBasePath + paths.training.wizardAddAnswer.path,
          drawerBasePath + paths.training.wizardAddExercise.path,
          drawerBasePath + paths.training.wizardEditExercise.path,
          drawerBasePath + paths.training.wizardCompleteExercise.path
        ]
      }];
    } 
    // SUPERVISOR (NO WIZARD ROUTES)
    else if (user.role.isSupervisor) {
      return [];
    }
    // TRAINER WIZARD ROUTES
    else if (user.role.isTrainer) {
      return [{
        component: EventView,
        path: [
          drawerBasePath + paths.training.wizardCompleteEvent.path
        ]
      }, {
        component: EventEdit,
        path: [
          drawerBasePath + paths.training.wizardEditEvent.path
        ]
      }, {
        component: EventContainer,
        path: [
          drawerBasePath + paths.training.wizardStart.path,
          drawerBasePath + paths.training.wizardCreateEvent.path,
          drawerBasePath + paths.training.wizardSelectEvent.path
        ]
      }, {
        component: ExerciseContainer,
        path: [
          drawerBasePath + paths.training.wizardAddExercise.path,
          drawerBasePath + paths.training.wizardEditExercise.path,
          drawerBasePath + paths.training.wizardCompleteExercise.path
        ]
      }];
    }
  },

  getExerciseContainerRoutes: (basePath) => {
    const drawerBasePath = basePath + paths.training.base;
    return [{
      component: AnswerEdit,
      path: [
        drawerBasePath + paths.training.wizardAddAnswer.path,
      ]
    }, {
      component: ExerciseView,
      path: [
        drawerBasePath + paths.training.wizardCompleteExercise.path
      ]
    }, {
      component: ExerciseEdit,
      path: [
        drawerBasePath + paths.training.wizardAddExercise.path,
        drawerBasePath + paths.training.wizardEditExercise.path,
      ]
    }];
  },

  getBackAction: () => {

  },

  onSave: async (record, history, basePath, recordStore, user) => {
    const match = matchPath(history.location.pathname, {
      path: basePath + paths.training.base + paths.training.wizardPattern
    });
    let {step, id} = match ? match.params : {};
    step = parseInt(step);


    // from event editor
    if (step === 1) {
      history.push(makeLocation(
        basePath,
        'training',
        'wizardAddExercise',
        { eventId: record.id },
        history.location
      ));
    }


    // from exercise or answer editor
    else if (step === 2 || step === 3) {
      const eventId = record.eventId || 0;
      const exerciseId = step === 2 ? record.id : id;
      const eventRecord = await recordStore.getRecord(Types.Record.EVENT, eventId);

      // if skip exer or save exer and event date future -> complete
      if (step === 2 
        && (
          record.id === 0 
          || isAfter(parseISO(eventRecord.date), new Date())
          || user.role.isTrainer
        )
      ) {
        redirectToComplete(basePath, history, eventId, exerciseId);
      }
      // else -> check for answer / complete
      else {
        let answerIds = null;
        let afterAnswerId = null;
        if (step === 2) {
          answerIds = record.answers;
        } else if (step === 3) {
          const exercise = await recordStore.getRecord(Types.Record.EXERCISE, id); 
          answerIds = exercise.answers;
          afterAnswerId = record.id;
        }

        const incompleteAnswer = await findIncompleteAnswer(answerIds, afterAnswerId, recordStore);

        if (incompleteAnswer) {
          history.push(makeLocation(
            basePath,
            'training',
            'wizardAddAnswer',
            { exerciseId, answerId: incompleteAnswer.id},
            history.location
          ));
        } else {
          redirectToComplete(basePath, history, record.eventId, exerciseId);   
        }
      }
    }
  },
};